import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, Download, LogOut } from "lucide-react";
import { SiteAdmin } from "./AdminSite";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aip_admin_token";

// Tokens live in sessionStorage so they're cleared when the tab closes — a small but
// meaningful XSS-blast-radius reduction over localStorage. The token is also opaque
// (random 32-byte URL-safe string issued by the server) rather than the password.
export default function Admin() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });

  if (!token) return <Login onLogin={(t) => { try { sessionStorage.setItem(TOKEN_KEY, t); } catch { /* private mode */ } setToken(t); }} />;
  return <Dashboard token={token} onLogout={() => { try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ } setToken(""); }} />;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      onLogin(r.data.token);
      toast.success("Welcome, Architect.");
    } catch {
      toast.error("Invalid password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--void)] flex items-center justify-center p-4">
      <form onSubmit={submit} className="glass rounded-3xl border border-cyan-400/30 p-8 md:p-10 max-w-md w-full" data-testid="admin-login-form">
        <Link to="/" className="text-cyan-400 hover:text-cyan-300 text-xs font-orbitron tracking-widest uppercase inline-flex items-center gap-2 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </Link>
        <h1 className="font-orbitron text-3xl font-bold gradient-text mb-2">Admin</h1>
        <p className="text-gray-400 text-sm mb-6">Architect access only.</p>
        <input
          type="password"
          required
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-5 py-3.5 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60 mb-4"
          data-testid="admin-password-input"
        />
        <button
          type="submit"
          disabled={submitting}
          className="w-full btn-gradient text-white py-3.5 rounded-full font-semibold disabled:opacity-60"
          data-testid="admin-login-btn"
        >
          {submitting ? "Authenticating…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("site");
  const authHeaders = React.useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const logout = () => onLogout();

  return (
    <div className="min-h-screen bg-[var(--void)] text-gray-200 pt-12 pb-32 md:pb-40 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-orbitron tracking-widest uppercase">
            <ArrowLeft className="w-4 h-4" /> Site
          </Link>
          <button onClick={logout} className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
        <h1 className="font-orbitron text-4xl md:text-5xl font-bold gradient-text mb-8">Admin Console</h1>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-tabs">
          {[
            ["site", "Site & Stats"],
            ["reviews", "Reviews"],
            ["posts", "Newsletter Posts"],
            ["blog", "Blog"],
            ["chats", "Chats"],
            ["subs", "Subscribers"],
            ["arc", "ARC Requests"],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-5 py-2 rounded-full font-orbitron text-xs uppercase tracking-widest border transition-colors ${
                tab === id ? "bg-cyan-400/20 border-cyan-400/60 text-cyan-300" : "border-white/10 text-gray-400 hover:text-white"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "site" && <SiteAdmin authHeaders={authHeaders} onOpenSubs={() => setTab("subs")} />}
        {tab === "reviews" && <ReviewsAdmin authHeaders={authHeaders} />}
        {tab === "posts" && <PostsAdmin authHeaders={authHeaders} />}
        {tab === "blog" && <BlogAdmin authHeaders={authHeaders} />}
        {tab === "chats" && <ChatsAdmin authHeaders={authHeaders} />}
        {tab === "subs" && <SubsAdmin authHeaders={authHeaders} token={token} />}
        {tab === "arc" && <ArcAdmin authHeaders={authHeaders} />}
      </div>
    </div>
  );
}

function ReviewsAdmin({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ quote: "", reviewer: "", source: "", accent: "cyan", pinned: false, order: 0 });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(
    () => axios.get(`${API}/reviews`).then((r) => setItems(r.data || [])).catch(() => {}),
    [],
  );
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/reviews`, form, authHeaders);
      setForm({ quote: "", reviewer: "", source: "", accent: "cyan", pinned: false, order: 0 });
      toast.success("Review added.");
      load();
    } catch { toast.error("Failed to add review."); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await axios.delete(`${API}/admin/reviews/${id}`, authHeaders); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const togglePinned = async (r) => {
    try {
      await axios.patch(`${API}/admin/reviews/${r.id}`, { pinned: !r.pinned }, authHeaders);
      load();
    } catch { toast.error("Update failed."); }
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setEditForm({ quote: r.quote, reviewer: r.reviewer, source: r.source || "", accent: r.accent || "cyan", order: r.order ?? 0 });
  };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/admin/reviews/${editing}`, editForm, authHeaders);
      toast.success("Review updated.");
      cancelEdit();
      load();
    } catch { toast.error("Save failed."); }
  };

  const pinnedCount = items.filter((r) => r.pinned).length;
  const rotatingCount = Math.max(0, items.length - 2);

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <SeedQuickSetup authHeaders={authHeaders} />

      {/* How reviews appear on the homepage — small explainer */}
      <div className="glass rounded-2xl border border-cyan-400/20 bg-cyan-400/5 px-5 py-4 text-xs leading-relaxed text-cyan-100/90" data-testid="admin-reviews-explainer">
        <strong className="font-orbitron tracking-widest uppercase text-[10px] text-cyan-300 mr-2">How this looks on the homepage</strong>
        The 2 reviews tagged <em>pinned</em> appear as the anchor cards. Anything beyond that rotates through the slot beneath them every 6 seconds.
        {" "}<span className="text-cyan-300">{pinnedCount} pinned · {rotatingCount} rotating</span>
      </div>

      <form onSubmit={add} className="glass rounded-3xl border border-white/10 p-6 grid md:grid-cols-2 gap-4">
        <textarea
          required
          rows={3}
          placeholder="Quote"
          value={form.quote}
          onChange={(e) => setForm({ ...form, quote: e.target.value })}
          className="md:col-span-2 px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
          data-testid="admin-review-quote"
        />
        <input required placeholder="Reviewer name" value={form.reviewer} onChange={(e) => setForm({ ...form, reviewer: e.target.value })}
          className="px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
          data-testid="admin-review-reviewer" />
        <input placeholder="Source (optional)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
          className="px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
          data-testid="admin-review-source" />
        <select value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })}
          className="px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white focus:outline-none focus:border-cyan-400/60"
          data-testid="admin-review-accent">
          <option value="cyan">cyan accent</option>
          <option value="purple">purple accent</option>
          <option value="pink">pink accent</option>
        </select>
        <input type="number" placeholder="Order (lower = first)" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })}
          className="px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
          data-testid="admin-review-order" />
        <label className="md:col-span-2 inline-flex items-center gap-2.5 text-gray-300 text-sm cursor-pointer select-none px-2">
          <input
            type="checkbox"
            checked={form.pinned}
            onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
            className="w-4 h-4 rounded border-white/20 bg-black/40 accent-cyan-400"
            data-testid="admin-review-pinned"
          />
          <span>Pinned (homepage anchor card). Up to 2 reviews can be pinned at once.</span>
        </label>
        <button type="submit" className="md:col-span-2 btn-gradient text-white px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2" data-testid="admin-review-add-btn">
          <Plus className="w-4 h-4" /> Add Review
        </button>
      </form>

      <div className="space-y-3">
        {items.map((r) => {
          const isEditing = editing === r.id;
          return (
            <div key={r.id} className="glass rounded-2xl border border-white/10 p-5" data-testid={`admin-review-row-${r.id}`}>
              {isEditing ? (
                <div className="grid md:grid-cols-2 gap-3">
                  <textarea rows={3} value={editForm.quote} onChange={(e) => setEditForm({ ...editForm, quote: e.target.value })}
                    className="md:col-span-2 px-4 py-3 rounded-2xl bg-black/40 border border-cyan-400/40 text-white focus:outline-none focus:border-cyan-400/80"
                    data-testid={`admin-review-edit-quote-${r.id}`} />
                  <input value={editForm.reviewer} onChange={(e) => setEditForm({ ...editForm, reviewer: e.target.value })}
                    className="px-4 py-3 rounded-full bg-black/40 border border-cyan-400/40 text-white" data-testid={`admin-review-edit-reviewer-${r.id}`} />
                  <input value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                    className="px-4 py-3 rounded-full bg-black/40 border border-cyan-400/40 text-white" data-testid={`admin-review-edit-source-${r.id}`} />
                  <select value={editForm.accent} onChange={(e) => setEditForm({ ...editForm, accent: e.target.value })}
                    className="px-4 py-3 rounded-full bg-black/40 border border-cyan-400/40 text-white" data-testid={`admin-review-edit-accent-${r.id}`}>
                    <option value="cyan">cyan accent</option>
                    <option value="purple">purple accent</option>
                    <option value="pink">pink accent</option>
                  </select>
                  <input type="number" value={editForm.order} onChange={(e) => setEditForm({ ...editForm, order: parseInt(e.target.value || "0", 10) })}
                    className="px-4 py-3 rounded-full bg-black/40 border border-cyan-400/40 text-white" data-testid={`admin-review-edit-order-${r.id}`} />
                  <div className="md:col-span-2 flex gap-2 justify-end">
                    <button onClick={cancelEdit} className="px-4 py-2 rounded-full bg-white/5 text-gray-300 text-xs uppercase tracking-widest font-orbitron" data-testid={`admin-review-edit-cancel-${r.id}`}>Cancel</button>
                    <button onClick={saveEdit} className="px-5 py-2 rounded-full btn-gradient text-white text-xs uppercase tracking-widest font-orbitron" data-testid={`admin-review-edit-save-${r.id}`}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {r.pinned && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-400/50 text-cyan-300 text-[10px] font-orbitron tracking-wider uppercase" data-testid={`admin-review-pinned-badge-${r.id}`}>
                          Pinned · anchor
                        </span>
                      )}
                      <span className="text-[10px] font-orbitron uppercase tracking-widest text-gray-500">accent: {r.accent || "cyan"}</span>
                      <span className="text-[10px] font-orbitron uppercase tracking-widest text-gray-500">order: {r.order ?? 0}</span>
                    </div>
                    <p className="text-gray-200 italic">"{r.quote}"</p>
                    <p className="text-gray-500 text-sm mt-2">— {r.reviewer}{r.source ? `, ${r.source}` : ""}</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button onClick={() => togglePinned(r)} className={`text-[10px] uppercase tracking-widest font-orbitron px-3 py-1.5 rounded-full border ${r.pinned ? "border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10" : "border-white/15 text-gray-400 hover:text-white hover:border-white/30"}`} data-testid={`admin-review-toggle-pin-${r.id}`}>
                      {r.pinned ? "Unpin" : "Pin"}
                    </button>
                    <button onClick={() => startEdit(r)} className="text-[10px] uppercase tracking-widest font-orbitron px-3 py-1.5 rounded-full border border-white/15 text-gray-300 hover:text-white hover:border-white/30" data-testid={`admin-review-edit-${r.id}`}>
                      Edit
                    </button>
                    <button onClick={() => del(r.id)} className="text-rose-400 hover:text-rose-300 inline-flex items-center justify-center" data-testid={`admin-review-del-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * SeedQuickSetup — at the top of the Reviews tab (the default landing tab in the
 * admin console) so the seed buttons are impossible to miss. Each row shows the
 * current count of items in a collection and lets the author seed if empty.
 */
function SeedQuickSetup({ authHeaders }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/seed-status`, authHeaders).then((r) => setStatus(r.data)).catch(() => {});
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const seedBlog = async () => {
    setBusy("blog");
    try {
      const r = await axios.post(`${API}/admin/blog/seed`, {}, authHeaders);
      if (r.data.skipped) toast.info(r.data.reason);
      else toast.success(`Seeded ${r.data.count} starter blog essay${r.data.count === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed."); }
    finally { setBusy(""); }
  };

  const seedNewsletter = async () => {
    setBusy("newsletter");
    try {
      const r = await axios.post(`${API}/admin/newsletter-posts/seed`, {}, authHeaders);
      if (r.data.skipped) toast.info(r.data.reason);
      else toast.success(`Seeded ${r.data.count} starter newsletter issue.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed."); }
    finally { setBusy(""); }
  };

  if (!status) return null;
  // Only show the banner when at least one collection is empty — once everything
  // is seeded it folds itself away so the admin isn't permanent noise.
  const needsAny = (status.blog_posts ?? 0) === 0 || (status.newsletter_posts ?? 0) === 0;
  if (!needsAny) return null;

  return (
    <div className="glass rounded-3xl border border-amber-400/40 bg-amber-400/5 p-5 md:p-6 space-y-4" data-testid="admin-seed-quicksetup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-orbitron text-[10px] tracking-[0.3em] text-amber-300 uppercase mb-1">First-time setup</p>
          <h2 className="text-white text-lg md:text-xl font-semibold">Seed starter content</h2>
          <p className="text-amber-100/80 text-sm mt-1 max-w-2xl">
            Fresh deploys start with empty Blog and Newsletter collections. Seed the curated starter content (3 Rodney Brooks essays for the blog, 1 Daniela Rus issue for the newsletter) with one click.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 flex items-center justify-between gap-3" data-testid="admin-seed-blog-row">
          <div>
            <p className="text-white font-semibold text-sm">Blog essays</p>
            <p className="text-gray-400 text-xs mt-0.5">{status.blog_posts} currently in DB</p>
          </div>
          <button
            onClick={seedBlog}
            disabled={!!busy || status.blog_posts > 0}
            className="px-4 py-2 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-200 text-xs font-orbitron uppercase tracking-widest hover:bg-amber-400/25 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-seed-blog-btn"
            title={status.blog_posts > 0 ? "Already seeded" : "Generate 3 starter essays"}
          >
            {busy === "blog" ? "Seeding…" : status.blog_posts > 0 ? "Seeded ✓" : "Seed blog →"}
          </button>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 flex items-center justify-between gap-3" data-testid="admin-seed-newsletter-row">
          <div>
            <p className="text-white font-semibold text-sm">Newsletter issues</p>
            <p className="text-gray-400 text-xs mt-0.5">{status.newsletter_posts} currently in DB</p>
          </div>
          <button
            onClick={seedNewsletter}
            disabled={!!busy || status.newsletter_posts > 0}
            className="px-4 py-2 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-200 text-xs font-orbitron uppercase tracking-widest hover:bg-amber-400/25 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-seed-newsletter-btn"
            title={status.newsletter_posts > 0 ? "Already seeded" : "Generate 1 starter newsletter issue"}
          >
            {busy === "newsletter" ? "Seeding…" : status.newsletter_posts > 0 ? "Seeded ✓" : "Seed newsletter →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PostsAdmin({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", cover_image: "" });
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null); // post id currently being edited
  const [editForm, setEditForm] = useState({});

  const load = useCallback(
    () => axios.get(`${API}/admin/newsletter-posts`, authHeaders).then((r) => setItems(r.data || [])).catch(() => {}),
    [authHeaders],
  );
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/newsletter-posts`, form, authHeaders);
      setForm({ title: "", excerpt: "", body: "", cover_image: "" });
      toast.success("Post published.");
      load();
    } catch { toast.error("Failed to publish."); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await axios.delete(`${API}/admin/newsletter-posts/${id}`, authHeaders); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const generate = async (style) => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/newsletter-posts/generate`, style ? { style } : {}, authHeaders);
      toast.success(`Draft generated: "${r.data.title}"`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generation failed.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const seedStarter = async () => {
    const hasContent = (items || []).length > 0;
    const force = hasContent
      ? window.confirm(`There are already ${items.length} newsletter post(s) here. Seed the starter Rodney Brooks issue anyway?`)
      : true;
    if (hasContent && !force) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/newsletter-posts/seed`, { force: hasContent }, authHeaders);
      if (r.data.skipped) {
        toast.info(r.data.reason);
      } else {
        toast.success(`Seeded ${r.data.count} starter newsletter issue.`);
      }
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Seed failed.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (id) => {
    try { await axios.post(`${API}/admin/newsletter-posts/${id}/publish`, {}, authHeaders); toast.success("Published."); load(); }
    catch { toast.error("Publish failed."); }
  };
  const unpublish = async (id) => {
    try { await axios.post(`${API}/admin/newsletter-posts/${id}/unpublish`, {}, authHeaders); toast.success("Moved to drafts."); load(); }
    catch { toast.error("Action failed."); }
  };
  const sendToSubs = async (p) => {
    if (!window.confirm(`Send "${p.title}" by email? Only subscribers who EXPLICITLY opted in to AI / newsletter content will receive this. This cannot be undone.`)) return;
    try {
      const r = await axios.post(`${API}/admin/newsletter-posts/${p.id}/send`, {}, authHeaders);
      const queued = r.data.queued || 0;
      const skipped = r.data.skipped_no_opt_in || 0;
      if (queued === 0) {
        toast.warning(`No one to send to — 0 subscribers have opted in to the newsletter yet.`, {
          description: `${skipped} subscriber(s) signed up for launch alerts only.`,
        });
      } else {
        toast.success(`Queued for ${queued} opted-in subscriber${queued === 1 ? "" : "s"}.`, {
          description: skipped > 0 ? `${skipped} launch-only subscriber${skipped === 1 ? "" : "s"} skipped (no opt-in).` : undefined,
        });
      }
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed.";
      toast.error(msg);
    }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setEditForm({ title: p.title, excerpt: p.excerpt || "", body: p.body || "", cover_image: p.cover_image || "" });
  };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/admin/newsletter-posts/${editing}`, editForm, authHeaders);
      toast.success("Saved.");
      cancelEdit();
      load();
    } catch { toast.error("Save failed."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-posts">
      {/* AI Auto-Generate panel */}
      <div className="glass rounded-3xl border border-cyan-400/30 p-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-orbitron text-xs text-cyan-400 uppercase tracking-widest mb-1">Welcome to The Awakening — Auto-curator</p>
            <p className="text-gray-300 text-sm">Pull from TechCrunch AI · MIT Tech Review · The Verge · Wired · VentureBeat. Auto-runs every 3 days. Drafts only — you approve before sending.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => generate(null)} disabled={generating}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gradient text-white font-semibold text-sm disabled:opacity-50"
            data-testid="admin-post-generate-auto">
            {generating ? "Generating…" : "Generate now (auto style)"}
          </button>
          {["cognito", "author", "roundup"].map((s) => (
            <button key={s} onClick={() => generate(s)} disabled={generating}
              className="px-4 py-2.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 text-gray-300 text-xs uppercase tracking-widest font-orbitron disabled:opacity-50"
              data-testid={`admin-post-generate-${s}`}>
              {s === "cognito" ? "Cognito's POV" : s === "author" ? "Author voice" : "Plain roundup"}
            </button>
          ))}
          <button onClick={seedStarter} disabled={generating}
            className="ml-auto px-4 py-2.5 rounded-full bg-amber-400/10 border border-amber-400/40 hover:border-amber-400/70 text-amber-200 text-xs uppercase tracking-widest font-orbitron disabled:opacity-50"
            data-testid="admin-newsletter-seed-starter"
            title="Re-create the Rodney Brooks / MIT starter issue (useful after a fresh deploy)">
            Seed starter content
          </button>
        </div>
      </div>

      {/* Manual post composer */}
      <details className="glass rounded-3xl border border-white/10">
        <summary className="cursor-pointer px-6 py-4 text-white font-orbitron text-sm">+ Write a post manually</summary>
        <form onSubmit={add} className="p-6 pt-2 space-y-4">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
            data-testid="admin-post-title" />
          <input placeholder="Cover image URL (optional)" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
            className="w-full px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
            data-testid="admin-post-cover" />
          <textarea rows={2} placeholder="Excerpt (optional)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
            data-testid="admin-post-excerpt" />
          <textarea required rows={8} placeholder="Body (plain text, new lines preserved)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/60"
            data-testid="admin-post-body" />
          <button type="submit" className="btn-gradient text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2" data-testid="admin-post-publish-btn">
            <Plus className="w-4 h-4" /> Publish
          </button>
        </form>
      </details>

      <div className="space-y-3">
        {items.map((p) => {
          const isDraft = !p.published;
          const isSent = !!p.sent_to_subscribers_at;
          const isEditing = editing === p.id;
          return (
            <div key={p.id} className="glass rounded-2xl border border-white/10 p-5" data-testid={`admin-post-row-${p.id}`}>
              {!isEditing ? (
                <>
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {isDraft ? (
                          <span className="text-[10px] font-orbitron uppercase tracking-widest text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5">Draft</span>
                        ) : (
                          <span className="text-[10px] font-orbitron uppercase tracking-widest text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5">Published</span>
                        )}
                        {isSent && <span className="text-[10px] font-orbitron uppercase tracking-widest text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-2 py-0.5">Sent</span>}
                        {p.auto_generated && <span className="text-[10px] font-orbitron uppercase tracking-widest text-purple-300 bg-purple-400/10 border border-purple-400/30 rounded-full px-2 py-0.5">AI · {p.style || "auto"}</span>}
                      </div>
                      <p className="text-white font-orbitron text-base md:text-lg">{p.title}</p>
                      <p className="text-gray-500 text-xs">{p.slug} · {new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <button onClick={() => del(p.id)} className="text-rose-400 hover:text-rose-300" data-testid={`admin-post-del-${p.id}`}>
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  {p.excerpt && <p className="text-gray-400 text-sm mb-3">{p.excerpt}</p>}
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line max-h-32 overflow-hidden mb-3" style={{ maskImage: "linear-gradient(180deg, #000 60%, transparent)" }}>
                    {(p.body || "").slice(0, 400)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => startEdit(p)} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 text-gray-300 text-xs" data-testid={`admin-post-edit-${p.id}`}>Edit</button>
                    {isDraft ? (
                      <button onClick={() => publish(p.id)} className="px-3 py-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 text-xs font-orbitron uppercase tracking-widest" data-testid={`admin-post-publish-${p.id}`}>Publish</button>
                    ) : (
                      <button onClick={() => unpublish(p.id)} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-amber-400/40 text-gray-300 text-xs" data-testid={`admin-post-unpublish-${p.id}`}>Unpublish</button>
                    )}
                    {!isSent && (
                      <button onClick={() => sendToSubs(p)} className="px-3 py-1.5 rounded-full bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 text-xs font-orbitron uppercase tracking-widest" data-testid={`admin-post-send-${p.id}`}>Send to subscribers →</button>
                    )}
                    {!isDraft && (
                      <a href={`/newsletter/${p.slug}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 text-gray-300 text-xs">View on site ↗</a>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-3" data-testid={`admin-post-edit-form-${p.id}`}>
                  <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white" />
                  <input placeholder="Cover image URL" value={editForm.cover_image} onChange={(e) => setEditForm({ ...editForm, cover_image: e.target.value })}
                    className="w-full px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white" />
                  <textarea rows={2} placeholder="Excerpt" value={editForm.excerpt} onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white" />
                  <textarea rows={12} value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white" />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="btn-gradient text-white px-5 py-2 rounded-full font-semibold text-sm">Save</button>
                    <button onClick={cancelEdit} className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <p className="text-gray-500 italic">No posts yet. Click "Generate now" to create your first draft.</p>}
      </div>
    </div>
  );
}

function SubsAdmin({ authHeaders, token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/subscribers`, authHeaders)
      .then((r) => setData(r.data))
      .catch(() => setErr("Failed to load subscribers."));
  }, [authHeaders]);

  return (
    <div className="space-y-6" data-testid="admin-subs">
      {err && <p className="text-rose-400">{err}</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total signups" value={data.total} />
            <Stat label="Newsletter opt-in" value={data.newsletter_opt_in ?? 0} />
            <Stat label="Launch-only" value={data.newsletter_opt_out ?? data.total} />
            <Stat label="Mailchimp" value={data.mailchimp_configured ? "Configured" : "Off"} />
          </div>
          <div className="glass rounded-2xl border border-amber-400/30 bg-amber-400/5 px-5 py-3 text-amber-200 text-xs leading-relaxed" data-testid="admin-optin-notice">
            <strong className="text-amber-300 font-orbitron tracking-widest uppercase text-[10px] mr-2">Consent rule</strong>
            Newsletter sends only fan out to subscribers who explicitly ticked the AI-content checkbox at signup. Launch-only subscribers are never auto-emailed newsletter blasts.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={`${API}/admin/subscribers.csv`}
              onClick={(e) => {
                // Fetch as authenticated, then download
                e.preventDefault();
                fetch(`${API}/admin/subscribers.csv`, { headers: { "X-Admin-Token": token } })
                  .then((r) => r.text())
                  .then((csv) => {
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "subscribers.csv"; a.click();
                    URL.revokeObjectURL(url);
                  });
              }}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm"
              data-testid="admin-csv-download"
            >
              <Download className="w-4 h-4" /> Download CSV
            </a>
            <button
              onClick={async () => {
                if (!window.confirm("Push every subscriber in MongoDB into your Klaviyo list? Safe to run repeatedly — Klaviyo de-dupes by email.")) return;
                try {
                  const r = await axios.post(
                    `${API}/admin/subscribers/klaviyo-backfill`, {}, authHeaders
                  );
                  const { total, pushed, failed } = r.data;
                  toast.success(`Klaviyo sync complete: ${pushed}/${total} pushed`, {
                    description: failed > 0 ? `${failed} failed — check server logs for details.` : "All subscribers now in your Klaviyo list.",
                  });
                } catch (err) {
                  toast.error("Klaviyo sync failed", {
                    description: err.response?.data?.detail || err.message,
                  });
                }
              }}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-purple-500/15 hover:bg-purple-500/25 border border-purple-400/40 text-purple-200 text-sm font-orbitron uppercase tracking-widest"
              data-testid="admin-klaviyo-backfill"
            >
              Sync to Klaviyo
            </button>
          </div>
          <div className="glass rounded-3xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-500 uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left p-4">Email</th>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">Newsletter</th>
                  <th className="text-left p-4">Source</th>
                  <th className="text-left p-4">Campaign</th>
                  <th className="text-left p-4">When</th>
                </tr>
              </thead>
              <tbody>
                {data.subscribers.map((s) => (
                  <tr key={s.email} className="border-t border-white/5" data-testid={`admin-sub-row-${s.email}`}>
                    <td className="p-4 text-gray-200">{s.email}</td>
                    <td className="p-4 text-gray-400">{s.name || "—"}</td>
                    <td className="p-4">
                      {s.newsletter_opt_in ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 text-xs font-orbitron tracking-wider uppercase" data-testid={`admin-sub-optin-${s.email}`}>
                          Opted in
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/5 border border-white/15 text-gray-500 text-xs font-orbitron tracking-wider uppercase" data-testid={`admin-sub-optout-${s.email}`}>
                          Launch only
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-gray-400">
                      {s.source ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-xs font-orbitron tracking-wider uppercase">
                          {s.source}
                        </span>
                      ) : <span className="text-gray-600">direct</span>}
                      {s.medium && <span className="text-gray-600 text-xs ml-2">/ {s.medium}</span>}
                    </td>
                    <td className="p-4 text-gray-500 text-xs">{s.campaign || "—"}</td>
                    <td className="p-4 text-gray-500">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass rounded-2xl border border-white/10 p-5">
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{label}</p>
      <p className="font-orbitron text-2xl text-white">{value}</p>
    </div>
  );
}


function ArcAdmin({ authHeaders }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(
    () => axios.get(`${API}/admin/arc-requests`, authHeaders)
      .then((r) => setData(r.data))
      .catch(() => setErr("Failed to load ARC requests.")),
    [authHeaders],
  );

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this ARC request?")) return;
    try { await axios.delete(`${API}/admin/arc-requests/${id}`, authHeaders); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="space-y-4" data-testid="admin-arc">
      {err && <p className="text-rose-400">{err}</p>}
      {data && (
        <>
          <p className="text-gray-500 text-sm">{data.total} request{data.total === 1 ? "" : "s"} total.</p>
          {data.items.length === 0 && (
            <p className="text-gray-500 italic">No ARC requests yet — share <span className="text-cyan-400">aipresidential.com/press</span> with reviewers and BookTok.</p>
          )}
          {data.items.map((it) => (
            <div key={it.id} className="glass rounded-2xl border border-white/10 p-5" data-testid={`admin-arc-row-${it.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-white font-orbitron">{it.name}</p>
                  <p className="text-gray-500 text-xs">
                    <a href={`mailto:${it.email}`} className="text-cyan-400 hover:text-cyan-300">{it.email}</a>
                    {it.outlet && <> · {it.outlet}</>}
                    {it.role && <> · {it.role}</>}
                    {it.audience_size && <> · {it.audience_size}</>}
                    {it.book && <> · wants <span className="text-cyan-400">{it.book}</span></>}
                  </p>
                </div>
                <button onClick={() => del(it.id)} className="text-rose-400 hover:text-rose-300" data-testid={`admin-arc-del-${it.id}`}>
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
              <p className="text-gray-300 whitespace-pre-line text-sm leading-relaxed">{it.pitch}</p>
              <p className="text-gray-600 text-xs mt-3">{new Date(it.created_at).toLocaleString()}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function BlogAdmin({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(
    () => axios.get(`${API}/admin/blog`, authHeaders).then((r) => setItems(r.data || [])).catch(() => {}),
    [authHeaders],
  );
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/blog/generate`, topic ? { topic } : {}, authHeaders);
      toast.success(`Draft generated: "${r.data.title}"`);
      setTopic("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally { setGenerating(false); }
  };

  const publish = async (id) => { try { await axios.post(`${API}/admin/blog/${id}/publish`, {}, authHeaders); toast.success("Published."); load(); } catch { toast.error("Failed."); } };
  const unpublish = async (id) => { try { await axios.post(`${API}/admin/blog/${id}/unpublish`, {}, authHeaders); toast.success("Moved to drafts."); load(); } catch { toast.error("Failed."); } };
  const del = async (id) => { if (!window.confirm("Delete this essay?")) return; try { await axios.delete(`${API}/admin/blog/${id}`, authHeaders); toast.success("Deleted."); load(); } catch { toast.error("Failed."); } };
  const delQA = async (postId, qaId) => { if (!window.confirm("Delete this Q&A?")) return; try { await axios.delete(`${API}/admin/blog/${postId}/qa/${qaId}`, authHeaders); toast.success("Removed."); load(); } catch { toast.error("Failed."); } };

  const seedStarter = async () => {
    const hasContent = (items || []).length > 0;
    const force = hasContent
      ? window.confirm(`There are already ${items.length} blog essay(s) here. Seed the 3 starter essays anyway? (They'll be added alongside existing ones.)`)
      : true;
    if (hasContent && !force) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/blog/seed`, { force: hasContent }, authHeaders);
      if (r.data.skipped) {
        toast.info(r.data.reason);
      } else {
        toast.success(`Seeded ${r.data.count} starter essay${r.data.count === 1 ? "" : "s"}.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed.");
    } finally { setGenerating(false); }
  };

  const startEdit = (p) => { setEditing(p.id); setEditForm({ title: p.title, excerpt: p.excerpt || "", body: p.body || "", cover_image: p.cover_image || "" }); };
  const saveEdit = async () => { try { await axios.patch(`${API}/admin/blog/${editing}`, editForm, authHeaders); toast.success("Saved."); setEditing(null); load(); } catch { toast.error("Failed."); } };

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="glass rounded-3xl border border-cyan-400/30 p-6 space-y-3">
        <p className="font-orbitron text-xs text-cyan-400 uppercase tracking-widest">Blog · AI essay generator</p>
        <p className="text-gray-300 text-sm">Generate a long-form essay (~800–1200 words) in Victor's voice. Drafts only — edit, publish, then visitors can ask The Archivist questions on the post.</p>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Optional topic hint (or leave blank)"
            className="flex-1 px-4 py-2.5 rounded-full bg-black/40 border border-white/10 text-white text-sm" data-testid="admin-blog-topic" />
          <button onClick={generate} disabled={generating}
            className="px-6 py-2.5 rounded-full btn-gradient text-white font-semibold text-sm disabled:opacity-50" data-testid="admin-blog-generate">
            {generating ? "Writing…" : "Generate draft"}
          </button>
          <button onClick={seedStarter} disabled={generating}
            className="px-5 py-2.5 rounded-full bg-amber-400/10 border border-amber-400/40 hover:border-amber-400/70 text-amber-200 text-xs uppercase tracking-widest font-orbitron disabled:opacity-50"
            data-testid="admin-blog-seed-starter"
            title="Generate the 3 starter essays (Cognito intro, Why I'm writing, Tour of the trilogy). Useful after a fresh deploy.">
            Seed starter content
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((p) => {
          const isDraft = !p.published;
          const isEditing = editing === p.id;
          return (
            <div key={p.id} className="glass rounded-2xl border border-white/10 p-5" data-testid={`admin-blog-row-${p.id}`}>
              {!isEditing ? (
                <>
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {isDraft
                          ? <span className="text-[10px] font-orbitron uppercase tracking-widest text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5">Draft</span>
                          : <span className="text-[10px] font-orbitron uppercase tracking-widest text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5">Published</span>}
                        {p.auto_generated && <span className="text-[10px] font-orbitron uppercase tracking-widest text-purple-300 bg-purple-400/10 border border-purple-400/30 rounded-full px-2 py-0.5">AI</span>}
                        {p.qa && p.qa.length > 0 && <span className="text-[10px] font-orbitron uppercase tracking-widest text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded-full px-2 py-0.5">{p.qa.length} Q&A</span>}
                      </div>
                      <p className="text-white font-orbitron text-base md:text-lg">{p.title}</p>
                      <p className="text-gray-500 text-xs">{p.slug} · {new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <button onClick={() => del(p.id)} className="text-rose-400 hover:text-rose-300" data-testid={`admin-blog-del-${p.id}`}><Trash2 className="w-5 h-5" /></button>
                  </div>
                  {p.excerpt && <p className="text-gray-400 text-sm mb-3">{p.excerpt}</p>}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button onClick={() => startEdit(p)} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 text-gray-300 text-xs" data-testid={`admin-blog-edit-${p.id}`}>Edit</button>
                    {isDraft
                      ? <button onClick={() => publish(p.id)} className="px-3 py-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 text-xs font-orbitron uppercase tracking-widest" data-testid={`admin-blog-publish-${p.id}`}>Publish</button>
                      : <button onClick={() => unpublish(p.id)} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-amber-400/40 text-gray-300 text-xs">Unpublish</button>}
                    {!isDraft && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/40 text-gray-300 text-xs">View ↗</a>}
                  </div>
                  {p.qa && p.qa.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-cyan-400 cursor-pointer font-orbitron tracking-widest uppercase">Q&A ({p.qa.length})</summary>
                      <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                        {p.qa.slice().reverse().map((qa) => (
                          <div key={qa.id} className="border border-white/5 rounded-lg p-3 bg-black/30">
                            <div className="flex justify-between gap-2 mb-1">
                              <p className="text-white text-xs font-medium">{qa.question}</p>
                              <button onClick={() => delQA(p.id, qa.id)} className="text-rose-400 hover:text-rose-300 text-[10px] uppercase tracking-widest flex-shrink-0">Delete</button>
                            </div>
                            <p className="text-gray-400 text-xs leading-relaxed">{qa.answer.slice(0, 240)}{qa.answer.length > 240 ? "…" : ""}</p>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="w-full px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white" />
                  <input placeholder="Cover image URL" value={editForm.cover_image} onChange={(e) => setEditForm({ ...editForm, cover_image: e.target.value })} className="w-full px-4 py-3 rounded-full bg-black/40 border border-white/10 text-white" />
                  <textarea rows={2} placeholder="Excerpt" value={editForm.excerpt} onChange={(e) => setEditForm({ ...editForm, excerpt: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white" />
                  <textarea rows={18} value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} className="w-full px-4 py-3 rounded-2xl bg-black/40 border border-white/10 text-white text-sm" />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="btn-gradient text-white px-5 py-2 rounded-full font-semibold text-sm">Save</button>
                    <button onClick={() => setEditing(null)} className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <p className="text-gray-500 italic">No essays yet. Click "Generate draft" to write the first one.</p>}
      </div>
    </div>
  );
}

function ChatsAdmin({ authHeaders }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(
    () => axios.get(`${API}/admin/chats`, authHeaders).then((r) => setData(r.data)).catch(() => {}),
    [authHeaders],
  );
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this chat session?")) return;
    try { await axios.delete(`${API}/admin/chats/${id}`, authHeaders); toast.success("Deleted."); if (active === id) setActive(null); load(); }
    catch { toast.error("Failed."); }
  };

  if (!data) return <p className="text-gray-500">Loading…</p>;
  const sessions = data.items || [];
  const current = sessions.find((s) => s.id === active);

  return (
    <div className="grid md:grid-cols-[320px,1fr] gap-4" data-testid="admin-chats">
      <div className="space-y-2 max-h-[70vh] overflow-y-auto">
        <p className="text-gray-500 text-xs uppercase tracking-widest font-orbitron mb-2">{data.total} session{data.total === 1 ? "" : "s"}</p>
        {sessions.length === 0 && <p className="text-gray-500 italic text-sm">No conversations yet.</p>}
        {sessions.map((s) => {
          const last = s.messages?.[s.messages.length - 1];
          const first = s.messages?.find((m) => m.role === "user");
          return (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`w-full text-left rounded-2xl border p-4 transition-colors ${active === s.id ? "border-cyan-400/50 bg-cyan-400/5" : "border-white/10 hover:border-white/30"}`}
              data-testid={`admin-chat-${s.id}`}
            >
              <p className="text-white text-sm font-medium truncate">{first?.text || "(no messages)"}</p>
              <p className="text-gray-500 text-xs mt-1">{s.message_count || 0} msgs · {new Date(s.updated_at).toLocaleString()}</p>
              {last?.role === "assistant" && <p className="text-gray-400 text-xs mt-2 line-clamp-2 italic">{last.text.slice(0, 100)}…</p>}
            </button>
          );
        })}
      </div>

      <div className="glass rounded-2xl border border-white/10 p-5 min-h-[300px]">
        {!current && <p className="text-gray-500 italic">Pick a session on the left to view the full conversation.</p>}
        {current && (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-gray-500 text-xs font-orbitron uppercase tracking-widest">Session</p>
                <p className="text-white text-xs font-mono">{current.id.slice(0, 8)}…</p>
                <p className="text-gray-500 text-xs mt-1">{current.message_count} messages · started {new Date(current.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => del(current.id)} className="text-rose-400 hover:text-rose-300 text-xs uppercase tracking-widest" data-testid={`admin-chat-delete-${current.id}`}>Delete</button>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {(current.messages || []).map((m, i) => (
                <div key={i} className={m.role === "user" ? "ml-8 bg-cyan-400/10 border border-cyan-400/20 rounded-2xl p-3" : "mr-8 bg-white/5 border border-white/10 rounded-2xl p-3"}>
                  <p className="text-[10px] font-orbitron uppercase tracking-widest text-gray-500 mb-1">{m.role === "user" ? "Visitor" : "Archivist"}</p>
                  <p className="text-gray-200 text-sm whitespace-pre-wrap">{m.text}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

